import { EPTSessionType } from '@tableau/microview-communication-service/lib/src/channelDefinitions/SearchEPTChannelDefinitions';
import { SiteUrls } from '@tableau/shared-urls';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Location } from '~commonInfra/utils/Location';
import { TelemetryEvents } from '~src/commonInfra/telemetryEvents/TelemetryEvents';
import { searchTelemetry } from '~src/search/components/SearchTelemetry';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { ContentFiltersKeys } from '~src/spacesPlatform/models/ContentFilters';

import { OmniboxTelemetryEventSource } from '../common/Constants';
import { BindUI } from '../common/UIBinding';
import { OmniBoxItemType } from '../models/Types';
import { SearchEPTService } from '../services/SearchEPTService';
import { SearchService } from '../services/SearchService';
import { ContentSuggestionViewModel } from './ContentSuggestionViewModel';
import { QuerySuggestionViewModel } from './QuerySuggestionViewModel';
import { QuickLinksViewModel } from './QuickLinksViewModel';
import { IKeyboardNavigableItem, QuickSearchModule } from './QuickSearchModule';

export const InputDebounceMilliseconds = 200;

export class OmniboxV2ViewModel extends QuickSearchModule {
    private shouldSkipBlurDueToWindowBlur = false;
    private tabIsPressed = false;
    private server: SessionManagementService;
    private searchBoxHasFocus = false;

    public inSearchResultPage: boolean;

    @BindUI() public searchTerm = '';
    @BindUI(QuickSearchModule.buildKeyboardNavigationPath) public searchResultUIVisible = false;
    @BindUI() public isInputboxActive: boolean = false;

    // activeKeyboardNavigableItem is different from currentKeyboardNavigableItem.
    // activeKeyboardNavigableItem is used to refer to current keyboard navigable item in any module.
    @BindUI() public activeKeyboardNavigableItem: IKeyboardNavigableItem;
    // currentKeyboardNavigableItem is defined in the base class QuickSearchModule, used to refer to the current item in its own module.
    // One usage of currentKeyboardNavigableItem is that the view can use it to check whether to highlight a particular item in a list.
    @BindUI() public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    public searchBoxNavigationItem: IKeyboardNavigableItem = { ownerModule: this, getIdentifier: () => 'Omnibox-SearchBox' };

    public querySuggestionViewModel = new QuerySuggestionViewModel(this);
    public quickLinksViewModel = new QuickLinksViewModel(this);
    public contentSuggestionViewModel = new ContentSuggestionViewModel(this);

    public siteRole = '';

    public askingForCloseWithFocus: () => void;
    public askingForCloseWithoutFocus: () => void;

    public focusOnSearchBox: () => void;
    public onSearchTermChanged: (searchTerm: string) => void;

    constructor(omniboxViewModel?: OmniboxV2ViewModel) {
        super(omniboxViewModel);
        this.omniboxViewModel = this;
    }

    public initialize(shouldOpenWithFocusInSearchTextbox?: boolean): void {
        this.server = getSessionManagementServiceInstance();

        // this.recentSearches = this.getRecentSearches();
        searchTelemetry.ensureInitialization(this.server.isOnlineEnvironment());

        SearchService.instance.initialize();
        this.clearSearch(shouldOpenWithFocusInSearchTextbox);

        /* SearchService.instance.notifyAboutMoreDataUpdatesForCurrentSearch = (searchResult: SearchResult) => {
            // If there's more data update for current search, then force update the current search to refresh the view.
            if (this.searchResult === searchResult) {
                this.forceUpdateSearchResult();
            }
        }; */

        this.querySuggestionViewModel.initialize();
        this.quickLinksViewModel.initialize();
        this.contentSuggestionViewModel.initialize();
    }

    public onUnmount() {
        super.onUnmount();
        this.clearSearch();
    }

    public isKeyboardNavigable() {
        return this.searchResultUIVisible;
    }

    public buildKeyboardNavigationPathInOwnModule(): void {
        this.getFirstKeyboardNavigableItem().up = this.previousKeyboardNavigableModule.getLastKeyboardNavigableItem();
        this.getLastKeyboardNavigableItem().down = this.nextKeyboardNavigableModule.getFirstKeyboardNavigableItem();
    }

    public getFirstKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.searchBoxNavigationItem;
    }

    public getLastKeyboardNavigableItem(): IKeyboardNavigableItem {
        return this.searchBoxNavigationItem;
    }

    public onSearchBoxClicked() {
        if (this.inSearchResultPage) {
            this.searchBoxHasFocus = true;
            this.isInputboxActive = true;

            this.onSearchBoxTransitionEnd();
        }
    }

    public onSearchBoxFocus() {
        this.searchBoxHasFocus = true;
        this.isInputboxActive = true;

        if (!this.searchTerm && !searchTelemetry.isSearchFlowStarted()) {
            searchTelemetry.startSearchFlow(OmniboxTelemetryEventSource);
        }

        SearchService.instance.personalizationRefresh();
    }

    public onSearchBoxTransitionEnd() {
        if (this.isInputboxActive) {
            if (!this.searchTerm) {
                this.showSearchResultWithQuickLinks();
            } else {
                this.debouncedSearch();
            }
            this.switchToNextItem(this.searchBoxNavigationItem);
        }
    }

    public onSearchBoxBlur() {
        setTimeout(() => {
            if (this.tabIsPressed && !this.shouldSkipBlurDueToWindowBlur) {
                this.searchBoxHasFocus = false;

                if (this.searchResultUIVisible)
                    this.closeSearch(true);
            }
            this.shouldSkipBlurDueToWindowBlur = false;
            this.tabIsPressed = false;
        });
    }

    public onSearchTextChange(value: string) {
        this.searchTerm = value;
        this.debouncedSearch();
    }

    public onEscapePressed() {
        this.closeSearch(true);
        if (this.askingForCloseWithFocus) {
            this.askingForCloseWithFocus();
        }

        // when pressing escape button , text inside inputbox will be cleared but inputbox will still have focus
        this.isInputboxActive = true;

        if (this.inSearchResultPage) {
            this.searchTerm = '';
        }
    }

    public onEnterPressed() {
        this.activeKeyboardNavigableItem.ownerModule.selectItemByKeyboard();
    }

    public selectItemByKeyboard(): void {
        this.querySuggestionViewModel.hitPastQueryItemBySearchTerm(this.searchTerm);

        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: 'global',
            seeAll: true,
            index: -1,
            total: -1,
            searchTerm: this.searchTerm,
            contentId: '-1',
        });

        if (FeatureFlags.searchEPT) {
            SearchEPTService.instance.start(EPTSessionType.fullSearch, this.searchTerm);
        }

        if (!this.inSearchResultPage) {
            this.navigateToSearchResultPage(this.searchTerm);
        } else {
            if (this.onSearchTermChanged) {
                this.onSearchTermChanged(this.searchTerm);
            }
        }
        this.closeSearch();
    }

    public onArrowLeftPressed(preventDefault: () => void) {
        this.switchToNextItem(this.activeKeyboardNavigableItem.left);

        // If the search result is visible and current navigation item is not searchbox,
        // array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible && this.currentKeyboardNavigableItem !== this.searchBoxNavigationItem) {
            preventDefault();
        }
    }

    public onArrowRightPressed(preventDefault: () => void) {
        this.switchToNextItem(this.activeKeyboardNavigableItem.right);

        // If the search result is visible and current navigation item is not searchbox,
        // array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible && this.currentKeyboardNavigableItem !== this.searchBoxNavigationItem) {
            preventDefault();
        }
    }

    public onArrowDownPressed(preventDefault: () => void) {
        this.switchToNextItem(this.activeKeyboardNavigableItem.down);

        // If the search result is visible, then array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible) {
            preventDefault();
        }
    }

    public onArrowUpPressed(preventDefault: () => void) {
        this.switchToNextItem(this.activeKeyboardNavigableItem.up);

        // If the search result is visible, then array key should work on search result, instead of on the text box to move the cursor around.
        if (this.searchResultUIVisible) {
            preventDefault();
        }
    }

    public onTabKeyPressed(preventDefault: () => void) {
        this.tabIsPressed = true;
    }

    public closeSearch(searchFlowCanncelled?: boolean) {
        if (searchFlowCanncelled) {
            searchTelemetry.endSearchFlowWithNoResultEvent(OmniboxTelemetryEventSource, this.searchTerm);
        }

        this.clearSearch();
    }

    public onResultItemSelected(type: OmniBoxItemType, total: number, index: number, contentId: string, skipClearSearchFlowId?: boolean, seeAll: boolean = false) {
        searchTelemetry.sendSearchResultClickedEvent({
            type: TelemetryEvents.SearchResultClicked,
            eventSource: OmniboxTelemetryEventSource,
            resultType: type.toString(),
            seeAll: seeAll,
            index: index + 1,
            total,
            searchTerm: this.searchTerm,
            contentId,
        }, skipClearSearchFlowId);

        // In some scenario like when the data source search result item is selected by ***keyboard***, the search result will be closed
        // but the focus is still in search box. In this case, we need to keep the search box activated when clearing the search.
        // In order the check whether the search box has focus, we need to defer the check to next UI refresh cycle to make sure the view model already knows
        // whether the search box has focus or not since the property about whether search box has focus is updated by html focus/blur event which we don't have control
        // about which will be called first, this function or the focus/blur event handler function.
        setTimeout(() => {
            this.clearSearch(this.searchBoxHasFocus);
        });
    }

    public navigateToSearchResultPage(searchTerm: string) {
        Location.instance.setUrl(this.getUrlBySearchTerm(searchTerm));
    }

    public getUrlBySearchTerm(searchTerm: string) {
        const urls = new SiteUrls({ siteUrlName: SearchService.instance.Server.sessionSiteUrlName(), queryString: window.location.search });
        const url = (FeatureFlags.searchV2 ? urls.searchUrl() : urls.searchViewsUrl()) + '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(searchTerm);
        return url;
    }

    public onWindowBlurred() {
        this.shouldSkipBlurDueToWindowBlur = true;
    }

    public shouldUseColorHighlight(): boolean {
        if (this.querySuggestionViewModel.querySuggestionItems) {
            for (let i = 0; i < this.querySuggestionViewModel.querySuggestionItems.length; i++) {
                for (let j = 0; j < this.querySuggestionViewModel.querySuggestionItems[i].segments.length; j++) {
                    if (!this.supportsBold(this.querySuggestionViewModel.querySuggestionItems[i].segments[j].text)) {
                        return true;
                    }
                }
            }
        }

        if (this.contentSuggestionViewModel.contentSuggestionItems) {
            for (let i = 0; i < this.contentSuggestionViewModel.contentSuggestionItems.length; i++) {
                const currentItem = this.contentSuggestionViewModel.contentSuggestionItems[i];
                if (!this.supportsBold(currentItem.titleHtml) || !this.supportsBold(currentItem.ownerHtml)) {
                    return true;
                }
            }
        }

        return false;
    }

    private debouncedSearch = _.debounce(() => {
        let actualSearchText = this.searchTerm.trim();
        if (actualSearchText) {
            searchTelemetry.sendSearchTermUpdatedEvent(OmniboxTelemetryEventSource, actualSearchText);
            if (FeatureFlags.searchEPT) {
                SearchEPTService.instance.start(EPTSessionType.contentSuggestion, this.searchTerm);
            }
        }
        this.searchResultUIVisible = true;
        this.querySuggestionViewModel.generateQuerySuggestionItems();
        this.contentSuggestionViewModel.processSearchTerm(actualSearchText);
    }, InputDebounceMilliseconds);

    private showSearchResultWithQuickLinks() {
        this.searchResultUIVisible = true;
        this.querySuggestionViewModel.generateQuerySuggestionItems();
    }

    private clearSearch(shouldKeepSearchBoxActive?: boolean) {
        this.searchResultUIVisible = false;
        if (!this.inSearchResultPage) {
            this.searchTerm = '';
        }
        if (!shouldKeepSearchBoxActive) {
            this.isInputboxActive = false;
        }
    }

    private supportsBold(text: string): boolean {
        for (let i = 0; i < text.length; i++) {
            const charCode = text.charCodeAt(i);
            // The follwoing range is based on the document: src\omnibox\CharacterASCIICodeAndBoldFontFaceMapping.pdf
            // This document prints the characters from ASCII 1 to ASCII 9999 and the related characters which are applied with bold style based on VizPortal Client web page styles (including font faces).
            // After analyzing the list, the following ranges are concluded to indicate characters whose ASCIIs are in those ranges don't support bold.
            // Note: actual ASCII character set is much bigger than 1000 but characters after 1000 don't support bold well (e.g. Asian characters)
            if ((charCode >= 1573 && charCode <= 3646) || (charCode >= 4352 && charCode <= 7679) || charCode >= 8205) {
                return false;
            }
        }

        return true;
    }
}